import tenantData from '@data';
import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';
import { formatListingDetailResponse } from '@transformers/drawerListingDetail';

const buildListingMutationPayload = (params) =>
  tenantPayloads.getUpdateListingPayload({
    userId: params?.userId,
    values: params?.values,
    listing: params?.listing,
    autoRenew: params?.autoRenew,
    productId: params?.productId,
    source: params?.source,
    creditsInfo: params?.creditsInfo,
    dynamicFieldDefinitions: params?.dynamicFieldDefinitions,
  });

/**
 * Shared Surge post-listing RTK endpoints for non-Zameen tenants.
 * Uses @data / @payloads / @transformers (tenant build resolves aliases).
 * Bayut merges license-based `GET .../listings/new?` ahead of `postNewListing` in its local module.
 */
const surgePostListingEndpoints = {
  postNewListing: {
    query: (params) => {
      const payload = buildListingMutationPayload(params);
      return { url: `/api/surge/listings`, method: 'POST', body: payload };
    },
  },
  getAmenities: {
    query: (purposeId) => ({
      url: `/api/surge/amenities${purposeId ? `?purpose_id=${purposeId}` : ''}`,
    }),
    transformer: (response) => tenantTransformers.amenitiesDataMapper(response?.amenities),
  },
  getAutoGeneratedListingContent: {
    query: (body) => {
      const payload = tenantPayloads.autoGeneratedListingContentPayload(body);
      return {
        url: `/api/surge/listings/generate_content`,
        method: 'POST',
        body: payload,
      };
    },
  },
  getQualityCriteria: {
    query: () => ({
      url: `/api/surge/listings/listing_score_metrics`,
    }),
    transformer: (response) => response?.score_metrics,
  },
  getPriceCheck: {
    query: ({ values }) => {
      const areaUnitSlug = tenantData.areaUnitList.find((e) => e.id === values.area.unit)?.slug;
      const payload = {
        location_id: values.location_select?.location.location_id,
        type_id: values.property_type,
        purpose_id: values.purpose,
        area: (values.area.value * tenantData.areaMapings[areaUnitSlug]['square_feet']).toFixed(0),
        price: values?.monthly_rent.value ? values?.monthly_rent?.value : values.price.value,
      };
      return { url: `/api/surge/listings/price_check`, body: payload };
    },
  },
  getListingDetail: {
    query: ({ listingId }) => ({
      url: `/api/surge/listings/${listingId}/edit`,
    }),
    transformer: (response) => formatListingDetailResponse(response),
  },
  updateListing: {
    query: (params) => {
      const payload = buildListingMutationPayload(params);
      return { url: `/api/surge/listings/${params?.values?.id}`, method: 'PUT', body: payload };
    },
  },
  getListingCategories: {
    query: () => ({
      url: `/api/surge/listing_categories`,
    }),
    transformer: (response) => response?.listing_categories || [],
  },

  getSurgeFeatures: {
    query: ({ sourceId }) => ({
      url: `/api/surge/features?source_id=${sourceId}`,
      method: 'GET',
    }),
    transformer: (response) => {
      const list = Array.isArray(response?.features) ? response.features : [];
      return list
        .map((opt) => ({
          id: opt.id,
          label: opt.label ?? null,
          label_l1: opt.label_l1 ?? null,
          value: opt.value ?? null,
          value_l1: opt.value_l1 ?? null,
          slug: opt.slug ?? null,
          format_type: opt.format_type ?? null,
          dynamic_section: opt.dynamic_section ?? null,
          display_order: opt.display_order ?? 0,
          secondary_display_order: opt.secondary_display_order,
        }))
        .filter((o) => o.id != null);
    },
  },
};

export default surgePostListingEndpoints;
